import React from 'react';
import clsx from 'clsx';
import { createPluginFactory, getPluginOptions } from '@udecode/plate-common';

import { uploadEditorImageToS3 } from 'services/amazon';
import { generateUUID } from 'utils/general';
import { getFileData, getExtFromUrl, dataURLtoFile } from 'utils/images';
import { handleFileFromURL } from '../upload/extractImage';
import { withImage } from './withImage';
import { onDrop } from '../upload/handlers';
import { KEY_DOCUMENT_SETTINGS } from '../../document-settings/createDocumentSettingsPlugin';

export const ELEMENT_IMAGE = 'img';

/**
 * Enables support for images with widths.
 */
export const createImagePlugin = createPluginFactory({
  key: ELEMENT_IMAGE,
  isElement: true,
  isVoid: true,
  withOverrides: withImage,
  options: {
    allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  },
  handlers: {
    onDrop: (editor) => (e) => onDrop(editor, e),
  },
  serializeHtml: (props) => {
    const { element } = props;
    const { width, align, url, caption } = element;
    const style = { textAlign: align };
    const figcaptionStyle = {
      width: `${width}px`,
    };
    return (
      <figure
        style={style}
        className={clsx('slate-media', `slate-media--align-${align}`)}
      >
        <div className="slate-img">
          <img
            src={url}
            width={width}
          />
        </div>
        {caption?.length > 0 && (
          <figcaption style={figcaptionStyle}>
            {caption[0].text}
          </figcaption>
        )}
      </figure>
    );
  },
  then: (editor, { type }) => {
    return {
      deserializeHtml: {
        rules: [
          {
            validNodeName: 'IMG',
          },
        ],
        getNode: (el) => {
          // Need to ensure image has a width value otherwise will disappear if width is 0 after saving note.
          const elementWidth = el.getAttribute('width') || el.width;
          const styleWidth = el.style.width;
          const styleWidthValue = styleWidth && parseInt(styleWidth.split('px')[0], 10);
          const width = elementWidth > 0 ? elementWidth : styleWidthValue > 0 ? styleWidthValue : 100;

          const imgUrl = el.getAttribute('src');

          if (!imgUrl) return;

          // Test if url is a base 64 string and return the extension.
          let ext = testB64Url(imgUrl);

          // Assume url is web url (https://www...) if the above test fails.
          if (!ext) ext = getExtFromUrl(imgUrl);

          // If no extension is found, return.
          if (!ext) return;

          const filename = `${generateUUID()}.${ext}`;

          if (editor.cloud) {
            if (isBase64Url(imgUrl)) {
              const file = dataURLtoFile(imgUrl, filename);
              uploadFile(editor, file, filename);
            }
            else {
              // Need to try and fetch the image from the url and convert it to a file object to then upload to s3.
              try {
                handleFileFromURL(imgUrl, (result) => uploadFile(editor, result, filename), filename);
              } catch {
                // Do nothing and just use the original image without uploading.
              }
            }
          }
          return ({
            type,
            filename,
            url: el.getAttribute('src'),
            width: parseInt(width, 10),
          });
        },
      },
      props: {
        align: 'left',
        caption: { disabled: true },
      },
    };
  },
});

const testB64Url = (url) => {
  if (isBase64Url(url)) {
    const mimeType = getMimeTypeFromB64Url(url);
    let ext;
    switch (mimeType) {
      case 'image/png':
        ext = 'png';
        break;
      case 'image/jpeg':
        ext = 'jpg';
        break;
      case 'image/webp':
        ext = 'webp';
        break;
      case 'image/gif':
        ext = 'gif';
        break;
      default:
        break;
    }
    return ext;
  }
};

const isBase64Url = (url) => {
  // Check if the URL starts with 'data:' and contains ';base64,'
  const base64Pattern = /^data:(.*);base64,([a-zA-Z0-9+/=]+)$/;

  // Test the URL against the pattern
  return base64Pattern.test(url);
};

const getMimeTypeFromB64Url = (url) => {
    // Check if it's a valid Base64 data URL
    const regex = /^data:([^;]+);base64,/;
    const match = url.match(regex);

    // If a match is found, return the MIME type (group 1 in the match)
    if (match) {
        return match[1];  // The MIME type
    }

    // Return null if it's not a valid Base64 URL
    return null;
};

const uploadFile = async (editor, file, filename) => {
  let status;

  const [header, b64] = await getFileData(file);
  file.url = b64;

  const { imageUploadPath } = getPluginOptions(editor, KEY_DOCUMENT_SETTINGS);

  const dataUrl = await uploadEditorImageToS3(
    file,
    header,
    imageUploadPath,
    (value, error) => {
      status = value;
      let errMsg = typeof error === 'string' ? error : 'Something went wrong. Please try again.';

      if (error instanceof Error && error.message) {
        errMsg = error.message;
      }

      if (value === 'failed') {
        editor.cloud.imageFileHandlers?.onError?.(filename, errMsg);
      }
    },
    () => {
      editor.cloud.imageFileHandlers?.onStart?.(file, filename, false);
    },
    (percent) => {
      editor.cloud.imageFileHandlers?.onProgress?.(filename, percent);
    },
  );

  if (status === 'success') {
    editor.cloud.imageFileHandlers?.onSuccess?.(file, filename, dataUrl);
  }
};
